/**
 * Available teacher roles/groups in the system
 */
export const TEACHER_ROLES = {
  director: 'director',
  manager: 'manager',
} as const;

export type TeacherRole = (typeof TEACHER_ROLES)[keyof typeof TEACHER_ROLES];

/**
 * User role types for UI selection
 */
export enum UserRoleType {
  TEACHER = 'teacher',
  DIRECTOR = 'director',
  MANAGER = 'manager',
}

/**
 * Available user role options for UI selection
 */
export interface UserRoleOption {
  value: UserRoleType;
  label: string;
  groups: string[];
}

export const USER_ROLE_OPTIONS: UserRoleOption[] = [
  {
    value: UserRoleType.TEACHER,
    label: 'Učitel',
    groups: [],
  },
  {
    value: UserRoleType.MANAGER,
    label: 'Správce',
    groups: [TEACHER_ROLES.manager],
  },
  {
    value: UserRoleType.DIRECTOR,
    label: 'Ředitel',
    groups: [TEACHER_ROLES.director, TEACHER_ROLES.manager],
  },
];

/**
 * Helper function to determine UI role type from groups array
 */
export function getUIRoleFromGroups(groups: string[]): UserRoleType {
  if (groups.includes(TEACHER_ROLES.director) && groups.includes(TEACHER_ROLES.manager)) {
    return UserRoleType.DIRECTOR;
  }
  if (groups.includes(TEACHER_ROLES.manager)) {
    return UserRoleType.MANAGER;
  }
  return UserRoleType.TEACHER;
}

/**
 * Helper function to get groups array from UI role type
 */
export function getGroupsFromUIRole(roleType: UserRoleType): string[] {
  const option = USER_ROLE_OPTIONS.find((opt) => opt.value === roleType);
  return option ? option.groups : [];
}

/**
 * Normalizes role string from API into supported user role type.
 */
export function normalizeUserRole(role?: string | null): UserRoleType {
  switch ((role || '').toLowerCase()) {
    case UserRoleType.DIRECTOR:
      return UserRoleType.DIRECTOR;
    case UserRoleType.MANAGER:
      return UserRoleType.MANAGER;
    default:
      return UserRoleType.TEACHER;
  }
}
